import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FaBell,
  FaCheck,
  FaCheckDouble,
  FaTimes,
  FaTrash,
  FaUserPlus,
  FaExclamationTriangle,
  FaCreditCard,
  FaCalendarTimes,
  FaGlobe,
  FaUser,
  FaEnvelope,
  FaClock,
  FaShieldAlt
} from 'react-icons/fa';
import { apiHelper } from '../../utils/api';
import logger from '../../utils/logger';
import { useOverlayBack } from '../../hooks/useSmartBack';
import './AdminNotificationPanel.scss';

const NOTIFICATION_ICONS = {
  new_user: FaUserPlus,
  new_user_pending: FaUserPlus,
  failed_login: FaExclamationTriangle,
  plan_expired: FaCalendarTimes,
  payment_success: FaCreditCard,
  payment_failed: FaCreditCard,
};

const NOTIFICATION_COLORS = {
  new_user: '#10b981',
  new_user_pending: '#f59e0b',
  failed_login: '#ef4444',
  plan_expired: '#f59e0b',
  payment_success: '#10b981',
  payment_failed: '#ef4444',
};

const TYPE_LABELS = {
  new_user: 'New User Registration',
  new_user_pending: 'New Signup (Pending Verification)',
  failed_login: 'Failed Login Attempt',
  plan_expired: 'Plan Expired',
  payment_success: 'Payment Successful',
  payment_failed: 'Payment Failed',
};

const AdminNotificationPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const panelRef = useRef(null);
  const lastCountRef = useRef(0);
  const isFetchingCountRef = useRef(false);
  const backoffUntilRef = useRef(0);

  // Step-by-step back: a back press with the panel open closes it first.
  const closeAdminPanel = useCallback(() => setIsOpen(false), []);
  useOverlayBack(isOpen, closeAdminPanel, 'admin-notifications');

  const extractUnreadCount = (payload) => {
    if (typeof payload?.count === 'number') return payload.count;
    if (typeof payload?.data?.count === 'number') return payload.data.count;
    return 0;
  };

  const extractData = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.notifications)) return payload.notifications;
    return [];
  };

  const fetchUnreadCount = useCallback(async () => {
    if (isFetchingCountRef.current) return;
    if (Date.now() < backoffUntilRef.current) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    isFetchingCountRef.current = true;
    try {
      const data = await apiHelper.get('/admin/notifications/unread-count');
      const newCount = extractUnreadCount(data);
      setUnreadCount(newCount);
      lastCountRef.current = newCount;
      backoffUntilRef.current = 0;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 429) {
        const retryAfter = Number(error?.response?.headers?.['retry-after'] || 60);
        backoffUntilRef.current = Date.now() + Math.min(retryAfter * 1000, 120000);
      }
      logger.debug('Error fetching admin unread count', error);
    } finally {
      isFetchingCountRef.current = false;
    }
  }, []);

  const fetchNotifications = useCallback(async (isBackground = false) => {
    if (!isBackground) setIsLoading(true);
    try {
      const data = await apiHelper.get('/admin/notifications');
      const list = extractData(data);
      setNotifications(list);
      const count = typeof data?.unreadCount !== 'undefined' ? data.unreadCount : list.filter(n => !n.read).length;
      setUnreadCount(count);
      lastCountRef.current = count;
    } catch (error) {
      logger.debug('Error fetching admin notifications', error);
    } finally {
      if (!isBackground) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchUnreadCount();
    }, 30000);
    const handleVisible = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', fetchUnreadCount);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', fetchUnreadCount);
    };
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications(false);
      const refreshInterval = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        fetchNotifications(true);
      }, 30000);
      return () => clearInterval(refreshInterval);
    }
  }, [isOpen, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMarkAsRead = async (id) => {
    try {
      await apiHelper.post(`/admin/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      logger.debug('Error marking admin notification as read', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiHelper.post('/admin/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      logger.debug('Error marking all admin notifications as read', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiHelper.delete(`/admin/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n._id !== id));
    } catch (error) {
      logger.debug('Error deleting admin notification', error);
    }
  };

  const handleClearAll = async () => {
    if (notifications.length === 0) return;
    setIsClearing(true);
    try {
      await apiHelper.delete('/admin/notifications');
      setNotifications([]);
      setUnreadCount(0);
      lastCountRef.current = 0;
    } catch (error) {
      logger.debug('Error clearing admin notifications', error);
    } finally {
      setIsClearing(false);
    }
  };

  const handleItemClick = (notification) => {
    if (!notification.read) {
      handleMarkAsRead(notification._id);
    }
    setSelectedNotif(notification);
    setIsOpen(false);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatFullTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getIcon = (notification) => {
    const IconComponent = NOTIFICATION_ICONS[notification.type] || FaBell;
    return <IconComponent />;
  };

  return (
    <>
      <div className="admin-notif-container" ref={panelRef}>
        <button
          className={`admin-notif-bell ${unreadCount > 0 ? 'has-unread' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Admin Notifications"
        >
          <FaBell />
          {unreadCount > 0 && (
            <span className="admin-notif-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {isOpen && (
          <div className="admin-notif-panel">
            <div className="admin-notif-panel__header">
              <h3>Admin Notifications</h3>
              <div className="admin-notif-panel__header-actions">
                {unreadCount > 0 && (
                  <button className="mark-all-read-btn" onClick={handleMarkAllAsRead}>
                    <FaCheckDouble /> Mark all read
                  </button>
                )}
                <button className="close-btn" onClick={() => setIsOpen(false)} aria-label="Close">
                  <FaTimes />
                </button>
              </div>
            </div>

            <div className="admin-notif-panel__list">
              {isLoading ? (
                <div className="admin-notif-loading">Loading notifications...</div>
              ) : notifications.length === 0 ? (
                <div className="admin-notif-empty">
                  <FaBell />
                  <p>No notifications yet</p>
                </div>
              ) : (
                notifications.map(notification => (
                  <div
                    key={notification._id}
                    className={`admin-notif-item ${notification.read ? 'read' : 'unread'} clickable`}
                    onClick={() => handleItemClick(notification)}
                  >
                    <div
                      className="admin-notif-item__icon"
                      style={{ background: `${NOTIFICATION_COLORS[notification.type] || '#6366f1'}18`, color: NOTIFICATION_COLORS[notification.type] || '#6366f1' }}
                    >
                      {getIcon(notification)}
                    </div>
                    <div className="admin-notif-item__content">
                      <h4>{notification.title}</h4>
                      <p>{notification.message}</p>
                      <div className="admin-notif-item__meta">
                        {notification.type === 'failed_login' && notification.metadata?.context && (
                          <span className={`admin-notif-context-badge admin-notif-context-badge--${notification.metadata.context}`}>
                            {notification.metadata.context === 'admin_panel' ? <FaShieldAlt /> : <FaGlobe />}
                            {notification.metadata.context === 'admin_panel' ? 'Admin Panel' : 'Platform'}
                          </span>
                        )}
                        <span className="admin-notif-item__time">
                          {formatTime(notification.createdAt)}
                        </span>
                      </div>
                    </div>
                    <div className="admin-notif-item__actions">
                      {!notification.read && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notification._id); }}
                          title="Mark as read"
                        >
                          <FaCheck />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(notification._id); }}
                        title="Delete"
                        className="delete-btn"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {notifications.length > 0 && (
              <div className="admin-notif-panel__footer">
                <button
                  className="clear-all-btn"
                  onClick={handleClearAll}
                  disabled={isClearing}
                >
                  <FaTrash /> Clear all
                </button>
                <span className="admin-notif-count">{notifications.length} notification{notifications.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedNotif && (
        <div className="admin-notif-overlay" onClick={() => setSelectedNotif(null)}>
          <div className="admin-notif-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-notif-modal__header">
              <div
                className="admin-notif-modal__icon"
                style={{ background: `${NOTIFICATION_COLORS[selectedNotif.type] || '#6366f1'}18`, color: NOTIFICATION_COLORS[selectedNotif.type] || '#6366f1' }}
              >
                {getIcon(selectedNotif)}
              </div>
              <div className="admin-notif-modal__title-group">
                <span className="admin-notif-modal__type-label">{TYPE_LABELS[selectedNotif.type] || selectedNotif.type}</span>
                <h3>{selectedNotif.title}</h3>
              </div>
              <button className="admin-notif-modal__close" onClick={() => setSelectedNotif(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="admin-notif-modal__body">
              <p className="admin-notif-modal__message">{selectedNotif.message}</p>

              <div className="admin-notif-modal__details">
                {selectedNotif.relatedUser && (
                  <>
                    <div className="admin-notif-modal__detail-row">
                      <FaUser className="detail-icon" />
                      <span className="detail-label">User</span>
                      <span className="detail-value">{selectedNotif.relatedUser.name || 'Unknown'}</span>
                    </div>
                    <div className="admin-notif-modal__detail-row">
                      <FaEnvelope className="detail-icon" />
                      <span className="detail-label">Email</span>
                      <span className="detail-value">{selectedNotif.relatedUser.email || 'Unknown'}</span>
                    </div>
                  </>
                )}

                {selectedNotif.metadata?.ipAddress && (
                  <div className="admin-notif-modal__detail-row">
                    <FaGlobe className="detail-icon" />
                    <span className="detail-label">IP Address</span>
                    <span className="detail-value">{selectedNotif.metadata.ipAddress}</span>
                  </div>
                )}

                {selectedNotif.metadata?.context && (
                  <div className="admin-notif-modal__detail-row">
                    {selectedNotif.metadata.context === 'admin_panel' ? <FaShieldAlt className="detail-icon" /> : <FaGlobe className="detail-icon" />}
                    <span className="detail-label">Area</span>
                    <span className="detail-value">{selectedNotif.metadata.context === 'admin_panel' ? 'Admin Panel' : 'Platform Login'}</span>
                  </div>
                )}

                {selectedNotif.metadata?.reason && (
                  <div className="admin-notif-modal__detail-row">
                    <FaExclamationTriangle className="detail-icon" />
                    <span className="detail-label">Reason</span>
                    <span className="detail-value">{selectedNotif.metadata.reason}</span>
                  </div>
                )}

                {selectedNotif.metadata?.attemptCount && (
                  <div className="admin-notif-modal__detail-row">
                    <FaExclamationTriangle className="detail-icon" />
                    <span className="detail-label">Attempts</span>
                    <span className="detail-value">{selectedNotif.metadata.attemptCount}</span>
                  </div>
                )}

                {selectedNotif.metadata?.plan && (
                  <div className="admin-notif-modal__detail-row">
                    <FaCreditCard className="detail-icon" />
                    <span className="detail-label">Plan</span>
                    <span className="detail-value">{selectedNotif.metadata.plan}</span>
                  </div>
                )}

                {selectedNotif.metadata?.amount && (
                  <div className="admin-notif-modal__detail-row">
                    <FaCreditCard className="detail-icon" />
                    <span className="detail-label">Amount</span>
                    <span className="detail-value">₹{selectedNotif.metadata.amount}</span>
                  </div>
                )}

                {selectedNotif.metadata?.failureReason && (
                  <div className="admin-notif-modal__detail-row">
                    <FaTimes className="detail-icon" />
                    <span className="detail-label">Failure Reason</span>
                    <span className="detail-value">{selectedNotif.metadata.failureReason}</span>
                  </div>
                )}

                {selectedNotif.metadata?.tier && (
                  <div className="admin-notif-modal__detail-row">
                    <FaCalendarTimes className="detail-icon" />
                    <span className="detail-label">Expired Tier</span>
                    <span className="detail-value">{selectedNotif.metadata.tier}</span>
                  </div>
                )}

                <div className="admin-notif-modal__detail-row">
                  <FaClock className="detail-icon" />
                  <span className="detail-label">Timestamp</span>
                  <span className="detail-value">{formatFullTime(selectedNotif.createdAt)}</span>
                </div>
              </div>
            </div>

            <div className="admin-notif-modal__footer">
              <button className="admin-notif-modal__btn" onClick={() => setSelectedNotif(null)}>
                Close
              </button>
              <button
                className="admin-notif-modal__btn admin-notif-modal__btn--danger"
                onClick={() => { handleDelete(selectedNotif._id); setSelectedNotif(null); }}
              >
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminNotificationPanel;
